'use client';
import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { StarIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { LineItem } from 'shared/types/cart/LineItem';
import { Variant, Money, Product } from 'shared/types/product';
import ProductModal from 'components/modules/modal';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useETrackerAddandRemoveActions from 'helpers/etrackerEvents/useETrackerAddandRemoveActions';
import { useFormat } from 'helpers/hooks/useFormat';
import useGTM from 'helpers/hooks/useGTM';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import usePath from 'helpers/hooks/usePath';
import { percentageImage, ResolutionTags } from 'helpers/utils/constantStrings';
import { generateCampaignItemUrl, getModuleBackgroundStyle } from 'helpers/utils/helperFunctions';
import { generateProductUrl, getParentCategory } from 'helpers/utils/productUtils';
import { createMarkup } from 'helpers/utils/renderHtmlFile';
import { mediumDesktop } from 'helpers/utils/screensizes';
import UVPPrice from 'helpers/utils/uvpPrice';
import { isPriceGreaterThanOrEqual } from 'helpers/utils/uvpPriceComparison';
import { useAccount, useCart, useProduct } from 'frontastic';
import { disableAddToCartForUpsell } from 'frontastic/hooks/useAddtoCartCheck';
import { useLocale } from 'frontastic/hooks/useLocale';
import Image from 'frontastic/lib/image';
import defaultLoader from 'frontastic/lib/image/loaders/default';
import { WrapperImageCollection } from '../SpecialOfferHomePageComponent';
interface BonusUpsellSku {
  skuCode?: string;
  preSelected?: boolean;
  discountValues?: Money;
  displayType?: string;
}
interface BonusProductsProps {
  dataProps?: {
    modulePlaceholder?: {
      description?: string;
      headline?: string;
      headlineType?: string;
      internalName?: string;
    };
    productData?: Product & {
      bonusUpsellSkuList?: {
        upsell?: BonusUpsellSku[];
        bonus?: BonusUpsellSku[];
      };
    };
    wrapperImages?: WrapperImageCollection;
  };
  indexing: number;
}

interface currentVariant {
  [key: string]: string;
}

const BonusProductsModule: React.FC<BonusProductsProps> = ({ dataProps, indexing }) => {
  const { locale } = useLocale();
  const { path } = usePath();
  const { formatMessage } = useFormat({ name: 'product' });
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const [variant, setVariant] = useState<Variant>();
  const [bonusVariants, SetBonusVariants] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const { addBonusItem, addItem, data } = useCart();
  const { categories } = useProduct();
  const [isDesktopSize] = useMediaQuery(mediumDesktop);
  const currentBackgroundStyle = getModuleBackgroundStyle();
  const [showModal, setShowModal] = useState(false);
  const [showAddedUpsellItem, setShowAddedUpsellItem] = useState<boolean>(false);
  const [recentlyAddedItems, setRecentlyAddedItems] = useState<LineItem[]>([]);
  const [currentVariants, setCurrentVariants] = useState<currentVariant>();
  const [selectedProductsList, setSelectedProductsList] = useState<string[]>([]);
  const { trackInsertToBasket } = useETrackerAddandRemoveActions();
  const { account } = useAccount();

  const currencyCode = dataProps?.productData?.variants?.[0]?.price?.currencyCode;
  const fractionDigits = dataProps?.productData?.variants?.[0]?.price?.fractionDigits;
  const uvpPriceValue = dataProps?.productData?.variants?.[0]?.attributes?.uvp_price;
  let uvpPrice = null;

  if (typeof uvpPriceValue === 'string' && uvpPriceValue.startsWith('{')) {
    try {
      uvpPrice = JSON.parse(uvpPriceValue);
    } catch (error) {
      console.error('Failed to parse UVP price JSON', error);
      uvpPrice = null;
    }
  } else {
    uvpPrice = uvpPriceValue || null;
  }
  const showUVP = isPriceGreaterThanOrEqual(uvpPrice, dataProps?.productData?.variants?.[0]?.price);

  const handleVariantChange = (event: React.ChangeEvent<HTMLSelectElement>, productKey: any, skus: string[]) => {
    const selectedVariant = event.target.value;
    const tempProductList = selectedProductsList.filter((sku) => !skus.includes(sku));
    tempProductList?.push(selectedVariant as string);
    setSelectedProductsList(tempProductList);
    setCurrentVariants((prevSelectedVariants) => ({
      ...prevSelectedVariants,
      [productKey]: selectedVariant,
    }));
  };
  useEffect(() => {
    if (!dataProps?.productData) return;
    const currentVariantPath = path.split('/');
    const currentVariantSKU = currentVariantPath[3]?.split('?')[0];
    const currentVariantIndex = dataProps?.productData?.variants?.findIndex(({ sku }) => sku == currentVariantSKU);
    setVariant(dataProps?.productData?.variants?.[currentVariantIndex] ?? dataProps?.productData?.variants?.[0]);
  }, [dataProps?.productData, path]);

  const sourceProductData = dataProps?.productData;
  const listOfBonusSKus = useMemo(() => {
    return dataProps?.productData?.bonusUpsellSkuList?.bonus || [];
  }, [sourceProductData]);

  const bonusProducts = useMemo(() => {
    return listOfBonusSKus.map((upsellProductItem) => {
      const upsellProductSKU = upsellProductItem.skuCode;
      const upsellProductsList = sourceProductData?.products?.products?.results?.find((upsellProduct) => {
        return (
          upsellProductSKU === upsellProduct?.key ||
          (upsellProductSKU && upsellProduct?.skus?.includes(upsellProductSKU))
        );
      });
      return upsellProductsList;
    });
  }, [listOfBonusSKus, sourceProductData]);

  const findBonusProductImage = (sku: string): string => {
    if (!sku || !dataProps?.wrapperImages) return '';

    const imageSizeTag = isDesktopSize ? ResolutionTags?.productTeaserDesktop : ResolutionTags?.productTeaserMobile;

    const imageTags = imageSizeTag?.split(' ');

    const matchedImage = dataProps?.wrapperImages?.items?.find(
      (imageObj) =>
        imageObj?.positiveTags?.includes(sku?.toLowerCase()) &&
        imageTags?.every((tag) => imageObj?.positiveTags?.includes(tag)),
    );

    return matchedImage?.image?.url || '';
  };

  useEffect(() => {
    if (bonusProducts && bonusProducts.length > 0) {
      const variantData = bonusProducts.map((product) => {
        const allVariants = product?.masterData?.current?.allVariants || [];
        return allVariants;
      });

      SetBonusVariants(variantData?.[0]?.[0]);
    }
  }, [dataProps?.productData, path]);

  useEffect(() => {
    if (
      dataProps?.productData?.bonusUpsellSkuList?.bonus &&
      dataProps?.productData?.bonusUpsellSkuList?.bonus?.length > 0
    ) {
      const tempProductList = [...selectedProductsList];
      dataProps?.productData?.bonusUpsellSkuList?.bonus?.forEach((bonusList) => {
        if (bonusList?.preSelected == true) {
          tempProductList?.push(bonusList?.skuCode as string);
        }
      });
      setSelectedProductsList(tempProductList);
    }
  }, []);

  const addToCartHandler = async () => {
    setLoading(true);
    try {
      const currentUrl: string = window?.location?.href;
      const parsedUrl = new URL(currentUrl);
      const getRelativeUrl = (parsedUrl?.pathname + parsedUrl?.search + parsedUrl?.hash)
        .replace(locale, '')
        .replace('//', '/');

      if (variant) {
        await addItem(variant, 1, getRelativeUrl, selectedProductsList?.length > 0);
      }
      trackInsertToBasket({
        product: dataProps?.productData,
        productVariant: dataProps?.productData?.variants?.[0],
        quantity: 1,
      });
      const bonusProductKey = bonusProducts?.[0]?.key;
      const currentBonusProduct = dataProps?.productData?.bonusUpsellSkuList?.bonus?.find((product) => {
        return product?.skuCode == bonusProductKey;
      }) as BonusUpsellSku;
      const parentSku = dataProps?.productData?.variants[0]?.sku;
      const bonusVariant = {
        sku: currentBonusProduct?.skuCode as string,
      };
      if (selectedProductsList?.length > 0 && parentSku) {
        const bonusProductsUrl = generateCampaignItemUrl(bonusProducts, selectedProductsList, categories, 'bonus');
        const isBonusItemAdded = await addBonusItem(parentSku, undefined, selectedProductsList, bonusProductsUrl);
        if (isBonusItemAdded) {
          setShowAddedUpsellItem(true);
          setShowModal(true);
        } else {
          setShowModal(false);
          toast.error(
            `${formatProductMessage({
              id: 'items.empty',
              defaultMessage: 'Item is not Added in Cart.please try again!',
            })}`,
          );
        }
      }
    } catch (error) {
      console.log('Bonus Product Add Item', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (showAddedUpsellItem) {
      const filteredItems =
        data?.lineItems?.filter(
          (lineItem: LineItem) =>
            lineItem?.variant?.sku &&
            selectedProductsList?.includes(lineItem?.variant?.sku) &&
            lineItem?.lineItemType === 'bonus',
        ) ?? [];
      setRecentlyAddedItems(filteredItems);
      const parentLineItem = data?.lineItems?.find(
        (lineItem: LineItem) =>
          lineItem?.variant?.sku === variant?.sku &&
          lineItem?.campaignLineItemSkus &&
          lineItem?.campaignLineItemSkus?.length > 0,
      );
      if (parentLineItem) {
        setRecentlyAddedItems([parentLineItem, ...filteredItems]);
      }
    }
  }, [showAddedUpsellItem && data]);
  if (
    !dataProps?.productData?.bonusUpsellSkuList?.bonus ||
    dataProps?.productData?.bonusUpsellSkuList?.bonus?.length === 0
  ) {
    return null;
  }

  const pdpVariantSku = path.split('/').filter(Boolean).pop();
  const pdpVariant = sourceProductData?.variants?.find((variant) => variant?.sku === pdpVariantSku);
  const disableButton = disableAddToCartForUpsell(pdpVariant, locale);

  return (
    <div
      className={`mx-auto mb-10 h-fit max-w-screen-xl bg-cover px-30 pb-30 pt-20 lg:py-30 ${
        indexing % 2 === 0 ? currentBackgroundStyle : ''
      }`}
    >
      <div className="self-center text-center font-calibri text-30 font-bold text-blueTheme-black">
        {dataProps?.modulePlaceholder?.headline}
      </div>
      <div
        className="mx-auto mt-20 max-w-2xl text-center text-12 font-normal text-blueTheme-black"
        dangerouslySetInnerHTML={createMarkup(dataProps?.modulePlaceholder?.description)}
      />

      <div className="mt-20">
        <div className="grid grid-cols-12 gap-30">
          <div className="col-span-12 mr-15 md:mr-0 lg:col-span-9 lg:mr-0">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
              {listOfBonusSKus.length > 0 &&
                listOfBonusSKus?.map((bonusSkuInfo, index) => {
                  const bonusSkuId = bonusSkuInfo.skuCode as string;
                  const freeProducts = bonusProducts?.find((product) => {
                    return product?.key == bonusSkuId || (bonusSkuId && product?.skus?.includes(bonusSkuId));
                  });
                  const key = bonusSkuId;
                  const currentUpsellProduct = dataProps?.productData?.bonusUpsellSkuList?.upsell?.find((product) => {
                    return product?.skuCode == freeProducts?.key;
                  });

                  const currentVariantProduct = freeProducts?.masterData?.current?.allVariants?.find(
                    (allVariantProduct) => {
                      const sku = key ? currentVariants?.[key] : undefined;
                      return allVariantProduct?.sku === (sku ?? key);
                    },
                  );
                  const matchingImageUrl = findBonusProductImage(key);
                  const parentCategory = getParentCategory(freeProducts, categories);
                  const productUrl = generateProductUrl(parentCategory, freeProducts!, key);
                  const currentBonusProduct = dataProps?.productData?.bonusUpsellSkuList?.bonus?.find((product) => {
                    return product?.skuCode == currentVariantProduct?.sku;
                  }) as BonusUpsellSku;

                  const isDropdownDisplayType = currentBonusProduct?.displayType === 'dropdown';

                  const allVariants: any = freeProducts?.masterData?.current?.allVariants;
                  const transformedVariants = allVariants?.map((variant: any) => {
                    // Find the variantAttribute in attributesRaw
                    const variantAttribute = variant?.attributesRaw?.find(
                      (attr: any) => attr?.name === 'variantAttribute',
                    );

                    // Get the key from the variantAttribute value
                    const key = variantAttribute ? variantAttribute?.value?.key : null;
                    const attributeValue =
                      variant?.attributesRaw?.find((attr: any) => attr?.name === key)?.value || null;

                    return {
                      sku: variant?.sku,
                      value: attributeValue,
                    };
                  });
                  const bonusUpsellSkuList = dataProps?.productData?.bonusUpsellSkuList?.bonus;
                  const matchingSKUs = transformedVariants
                    .map((variant: { sku: string | undefined; value: any }) => {
                      const matchingBonus = bonusUpsellSkuList?.find(
                        (bonus) => bonus.skuCode === variant.sku && bonus.displayType === 'dropdown',
                      );

                      if (matchingBonus) return variant;
                      return null; // return null if no match is found
                    })
                    .filter((item: null) => item !== null); // filter out null values
                  const attributes: any = freeProducts?.masterData?.current?.allVariants;
                  const productMainAttribute = attributes?.reduce((acc: any, attr: { attributesRaw: any[] }) => {
                    const foundAttribute = attr?.attributesRaw?.find(
                      (item: { name: string }) => item.name === 'product_main_attribute',
                    );
                    return foundAttribute?.value || null;
                  }, null);

                  const attributeValue = productMainAttribute?.[locale] ?? '';
                  return (
                    <div
                      data-skucode={currentBonusProduct?.skuCode}
                      key={index}
                      className="bonus-line-item flex h-full flex-col justify-between"
                    >
                      <div className="w-full min-w-full rounded border border-solid border-blueTheme-grayLight bg-white py-10">
                        <div className="flex items-center justify-between px-10">
                          <div className="text-wrap flex flex-col text-16 font-bold text-blueTheme-black">
                            <div className="w-[250px] lg:h-70 lg:w-[150px]">
                              {freeProducts?.masterData?.current?.name}
                              <div>{attributeValue}</div>
                            </div>
                          </div>
                          <div className="flex gap-4">
                            <Image
                              src={percentageImage}
                              width={30}
                              height={30}
                              className="object-cover"
                              alt="arr"
                              loader={defaultLoader}
                            />
                          </div>
                        </div>
                        <div className="relative mt-5">
                          <div className="absolute left-0 top-0 w-full bg-blueTheme-red py-1 text-center text-24 font-bold text-blueTheme-white">
                            {formatMessage({
                              id: 'product.free',
                              defaultMessage: 'GRATIS!',
                            })}
                          </div>
                          <Link href={`${productUrl}`}>
                            <Image
                              className="h-full max-h-400 min-h-300 w-full object-contain lg:max-h-400"
                              src={matchingImageUrl || currentVariantProduct?.images?.[0]?.url}
                            />
                          </Link>
                        </div>
                        <div className="mt-10 flex justify-end px-10">
                          <div className="flex items-center gap-4">
                            {Array.from({ length: Number(5) })?.map((_, index: number) => {
                              return <StarIcon key={index} className="h-12 w-12 fill-blueTheme-black" />;
                            })}
                          </div>
                        </div>
                        <div
                          className="mt-2 line-clamp-3 px-10 text-12 text-blueTheme-black"
                          dangerouslySetInnerHTML={{ __html: freeProducts?.masterData?.current?.description || '' }}
                        />
                        {isDropdownDisplayType && matchingSKUs?.length > 1 ? (
                          <div className="flex justify-center p-10">
                            {}
                            <select
                              onChange={(event) =>
                                handleVariantChange(event, freeProducts?.key, freeProducts?.skus as string[])
                              }
                              className="w-[60%] cursor-pointer justify-center rounded-sm  border border-solid border-blueTheme-grayLight p-2 text-12 font-bold text-blueTheme-black"
                            >
                              {matchingSKUs?.map((variant: any, key: number) => (
                                <option key={key} value={variant?.sku}>
                                  {variant?.value?.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className=""> </div>
                        )}

                        <div className="mt-5 flex flex-col items-center justify-end gap-4 px-10 lg:flex-row">
                          <div className="text-center">
                            <span className="text-16 text-blueTheme-black">
                              {formatMessage({ id: 'price.instead', defaultMessage: 'statt' })}
                            </span>
                            {currentVariantProduct?.price !== currentBonusProduct?.discountValues?.centAmount && (
                              <span className="ml-2 text-16 text-blueTheme-black line-through">
                                {CurrencyHelpers.formatForCurrency(currentVariantProduct?.price as number, locale)}
                              </span>
                            )}
                          </div>
                          <div className="ml-10 text-40 font-bold text-blueTheme-black">
                            {CurrencyHelpers.formatForCurrency(0 as number, locale, currencyCode, fractionDigits)}
                          </div>
                        </div>

                        <div className="px-10 text-center text-12">
                          <span>
                            {' '}
                            {formatMessage({
                              id: 'product.bonus.plus',
                              defaultMessage: 'zzgl.',
                            })}
                          </span>{' '}
                          <span>
                            {CurrencyHelpers.formatForCurrency(
                              (currentBonusProduct?.discountValues?.centAmount || 0) as number,
                              locale,
                              currencyCode,
                              fractionDigits,
                            )}
                          </span>{' '}
                          <span>
                            {' '}
                            {formatMessage({
                              id: 'product.bonus.extraCharge',
                              defaultMessage: 'für Service & Verpackung',
                            })}
                          </span>
                        </div>
                      </div>
                      <div
                        className={`mt-10 flex w-full items-center justify-center rounded ${
                          disableButton ? 'bg-blueTheme-grayDark' : 'bg-blueTheme-black'
                        } py-6 text-blueTheme-white`}
                      >
                        <input
                          checked={disableButton ? false : selectedProductsList?.includes(bonusSkuId)}
                          disabled={disableButton}
                          className={`cursor-pointer ${disableButton ? 'cursor-not-allowed' : ''}`}
                          type="checkbox"
                          value={bonusSkuId}
                          id={`checkbox${index}`}
                          onChange={(e) => {
                            if (!disableButton && freeProducts) {
                              if (e.target.checked === true) {
                                const currentSku = bonusSkuId;
                                if (currentSku) {
                                  const tempProductList = [...selectedProductsList];
                                  tempProductList?.push(currentSku as string);
                                  setSelectedProductsList(tempProductList);
                                }
                              } else {
                                const updatedSelectedProductList = selectedProductsList?.filter(
                                  (selectedProductSku) => {
                                    return selectedProductSku !== bonusSkuId;
                                  },
                                );
                                setSelectedProductsList(updatedSelectedProductList);
                              }
                            }
                          }}
                        />
                        <label
                          htmlFor={`checkbox${index}`}
                          className="ml-10 cursor-pointer text-14 font-bold text-blueTheme-white"
                        >
                          {formatMessage({ id: 'product.select.label', defaultMessage: 'Produkt auswählen' })}
                        </label>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-3">
            <div className="flex h-full flex-col  justify-end">
              <div className="flex flex-col items-center justify-center gap-4 lg:flex-row">
                <div>
                  {showUVP && uvpPrice && (
                    <div className="self-end">
                      <span className="font-calibri text-16 text-blueTheme-black">
                        {formatMessage({ id: 'price.instead', defaultMessage: 'statt' })}
                      </span>
                      <span className="ml-4 font-calibri text-16 text-blueTheme-black line-through">
                        <UVPPrice variant={dataProps?.productData?.variants?.[0]} />
                      </span>
                    </div>
                  )}

                  {dataProps?.productData?.variants?.[0]?.discountedPrice &&
                    dataProps?.productData?.variants?.[0]?.price && (
                      <div className="flex w-full">
                        <span className="ml-5 text-12 font-normal text-blueTheme-black">
                          {formatMessage({
                            id: 'letzter.niedrigster.preis',
                            defaultMessage: 'Letzter niedrigster Preis',
                          })}
                          <span className="ml-4">
                            {CurrencyHelpers.formatForCurrency(dataProps?.productData?.variants?.[0]?.price as number)}*
                          </span>
                        </span>
                      </div>
                    )}
                </div>
                <div className="ml-10 text-40 font-bold text-blueTheme-black">
                  {CurrencyHelpers.formatForCurrency(
                    dataProps?.productData?.variants?.[0]?.discountedPrice ||
                      (dataProps?.productData?.variants?.[0]?.price as number),
                    locale,
                  )}
                  <sup>*</sup>
                </div>
              </div>
              <div className="my-5 text-center font-calibri text-12">
                <div>{formatMessage({ id: 'product.price.vat', defaultMessage: 'Preis für alle inkl. MwSt.' })}</div>
                <div>{formatMessage({ id: 'product.price.shipping', defaultMessage: 'zzgl. Versandkosten' })}</div>
              </div>
              <button
                className={`tv-upsell-product-cta flex w-full items-center justify-center rounded ${
                  disableButton
                    ? 'bg-blueTheme-grayDark'
                    : selectedProductsList?.length > 0
                    ? 'bg-blueTheme-black'
                    : 'bg-blueTheme-grayDark'
                } py-8 text-center text-18 font-bold text-blueTheme-white`}
                onClick={addToCartHandler}
                disabled={(selectedProductsList?.length > 0 ? false : true) || disableButton}
              >
                <Image
                  src={'https://res.cloudinary.com/dlwdq84ig/image/upload/v1709120760/gd4rqlatw0nqoy0xhhup.png'}
                  width={30}
                  height={30}
                  className="object-cover"
                  alt="arr"
                  loader={defaultLoader}
                />
                <span className="ml-5">
                  {loading
                    ? formatMessage({ id: 'loader.message', defaultMessage: 'Wird geladen..' })
                    : formatMessage({ id: 'product.cta.allCart.label', defaultMessage: 'Alles in den Warenkorb' })}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <ProductModal show={showModal} onClose={() => setShowModal(false)} items={recentlyAddedItems} />
    </div>
  );
};

export default BonusProductsModule;
